import type { CoreState } from './state'
import type { EditorIntent } from './intents'
import { blockGap, findBlock, gapOf, gapPoint, locate, plain, pointAt, rows } from './tree-read'
import { spliceBlocks } from './ops'
import { blockClass, isSelectableBlock, nodeSelection } from './block-selection'
import type { Block } from '@w/content-blocks/src/block'
import type { CorePoint } from './state'
import type { Gap } from './tree-read'

/**
 * Lexical `$restoreEmptyContainerParagraph`: a container a removal left with no blocks gets a paragraph where the
 * block was, and the caret goes into it; otherwise the caret takes the gap the removal left.
 */
function restoreEmptied(state: CoreState, gap: Gap): CorePoint {
  if (gap.siblings.length > 0 || !gap.parent) return gapPoint(gap)
  const paragraph: Block = { id: '', type: 'p', children: [] }
  spliceBlocks(state, gap.siblings, 0, 0, paragraph)
  return { blockId: paragraph.id, offset: 0 }
}

/**
 * The caret after selected list items went (a list holds no gap): the start of the item now at their place, else the end
 * of the item before; a list left without items goes too, and the caret takes the gap it left.
 */
function afterItemRemoval(state: CoreState, items: Gap): CorePoint {
  const list = items.parent
  if (items.siblings.length === 0 && list) {
    const home = blockGap(state.blocks, list.id)
    if (home) {
      spliceBlocks(state, home.siblings, home.index, 1)
      // A page left with only its header gets its body paragraph back, as removeTopLevel does.
      if (!home.parent && state.blocks.every(block => blockClass(block) === 'header')) {
        const paragraph: Block = { id: '', type: 'p', children: [] }
        spliceBlocks(state, state.blocks, state.blocks.length, 0, paragraph)
        return { blockId: paragraph.id, offset: 0 }
      }
      return restoreEmptied(state, home)
    }
  }
  const next = items.siblings[items.index]
  const entries = next ? rows([next]) : rows(items.siblings.slice(0, items.index)).slice(-1)
  const row = entries[0]
  return row ? pointAt(row, next ? 0 : plain(row.block).length) : gapPoint(items)
}

/**
 * Removes the document-level blocks in `[index, index + count)` except the page header, which no deletion removes (it
 * is edited in its own fields). Returns the caret: the gap the removal left, or an empty body paragraph when only the
 * header remains (Lexical `$restoreEmptyContainerParagraph`).
 */
export function removeTopLevel(state: CoreState, index: number, count: number): CorePoint {
  let kept = 0
  for (let at = index + count - 1; at >= index; at -= 1) {
    const block = state.blocks[at]
    if (block && blockClass(block) === 'header') kept += 1
    else spliceBlocks(state, state.blocks, at, 1)
  }
  if (state.blocks.every(block => blockClass(block) === 'header')) {
    const paragraph: Block = { id: '', type: 'p', children: [] }
    spliceBlocks(state, state.blocks, state.blocks.length, 0, paragraph)
    return { blockId: paragraph.id, offset: 0 }
  }
  return gapPoint({ siblings: state.blocks, index: index + kept })
}

/**
 * Removes a node selection's blocks by identity, wherever they sit, except the page header; deletion and replacement
 * (paste, typing) share it. Returns the caret: the gap the first of them left in its block list (a container left empty
 * gets a paragraph; list items place it as `afterItemRemoval`), or an empty body paragraph when only the header remains;
 * undefined when nothing but the header was selected.
 */
export function removeSelectedNodes(state: CoreState, ids: readonly string[]): CorePoint | undefined {
  const removable = ids.filter(id => { const block = findBlock(state.blocks, id); return block && blockClass(block) !== 'header' })
  const first = removable.length > 0 ? blockGap(state.blocks, removable[0]) : undefined
  if (!first) return undefined
  for (const id of removable) {
    const owner = blockGap(state.blocks, id)
    if (owner) spliceBlocks(state, owner.siblings, owner.index, 1)
  }
  if (state.blocks.every(block => blockClass(block) === 'header')) {
    const paragraph: Block = { id: '', type: 'p', children: [] }
    spliceBlocks(state, state.blocks, state.blocks.length, 0, paragraph)
    return { blockId: paragraph.id, offset: 0 }
  }
  const gap = { ...first, index: Math.min(first.index, first.siblings.length) }
  return first.parent?.type === 'list' ? afterItemRemoval(state, gap) : restoreEmptied(state, gap)
}

export function atomicGesture(state: CoreState, intent: EditorIntent): boolean {
  const selection = state.selection
  if (!selection) return false
  const entries = rows(state.blocks)
  const anchorGap = gapOf(state.blocks, selection.anchor)
  const focusGap = gapOf(state.blocks, selection.focus)
  // Both endpoints in document-level gaps; nested gaps are handled by their container commands.
  const root = anchorGap?.siblings === state.blocks && focusGap?.siblings === state.blocks
  const anchorIndex = anchorGap?.index ?? 0
  const focusIndex = focusGap?.index ?? 0
  // A node selection deletes by identity below; its gap endpoints need not cover its nodes.
  if (intent.type.startsWith('delete') && root && anchorIndex !== focusIndex && !selection.nodes?.length) {
    const point = removeTopLevel(state, Math.min(anchorIndex, focusIndex), Math.abs(anchorIndex - focusIndex))
    state.selection = { anchor: point, focus: point }
    return true
  }
  if (intent.type.startsWith('delete') && !selection.table && !root) {
    const anchor = entries[locate(entries, selection.anchor).row]
    const focus = entries[locate(entries, selection.focus).row]
    const table = anchor.parents.find((block) => block.type === 'table') ??
      focus.parents.find((block) => block.type === 'table')
    const outside = table && (anchor.parents.includes(table) ? focus : anchor)
    if (table && outside && !outside.parents.includes(table) && !plain(outside.block)) {
      const index = state.blocks.indexOf(table)
      if (index >= 0) {
        spliceBlocks(state, state.blocks, index, 1)
        const other = rows(state.blocks).find((entry) => entry.siblings === outside.siblings &&
          Math.abs(entry.siblings.indexOf(entry.block) - entry.siblings.indexOf(outside.block)) === 1 &&
          !plain(entry.block))
        if (other && !plain(other.block)) {
          spliceBlocks(state, outside.siblings, outside.siblings.indexOf(outside.block), 1)
        }
        const point = pointAt(other && !plain(other.block) ? other : outside, 0)
        state.selection = { anchor: point, focus: point }
        return true
      }
    }
  }
  // A node selection deletes all its nodes whatever the block kind (a text block's node point is no document-level gap).
  // Nested selected nodes go by identity from their own block list (Lexical NodeSelection.deleteNodes); the caret takes
  // the gap they leave there.
  const nested = selection.nodes?.length ? blockGap(state.blocks, selection.nodes[0]) : undefined
  if (intent.type.startsWith('delete') && nested && nested.siblings !== state.blocks) {
    const point = removeSelectedNodes(state, selection.nodes ?? [])
    if (point) state.selection = { anchor: point, focus: point }
    return true
  }
  // A caret at a block cursor inside a container deletes the atom or grid beside it, as at the document level.
  if (intent.type.startsWith('delete') && !root && !selection.nodes?.length && anchorGap && focusGap &&
    anchorGap.siblings === focusGap.siblings && anchorIndex === focusIndex) {
    const index = anchorIndex - (intent.type.endsWith('Backward') ? 1 : 0)
    const beside = anchorGap.siblings[index] as Block | undefined
    if (!beside || !['atom', 'grid'].includes(blockClass(beside))) return false
    spliceBlocks(state, anchorGap.siblings, index, 1)
    const point = restoreEmptied(state, { ...anchorGap, index })
    state.selection = { anchor: point, focus: point }
    return true
  }
  if (intent.type.startsWith('delete') && (root || selection.nodes?.length)) {
    const backward = intent.type.endsWith('Backward')
    // The page header is edited in its own fields; a node selection over it removes only the blocks after it.
    const nodes = new Set(selection.nodes?.filter(id => { const block = findBlock(state.blocks, id); return !block || blockClass(block) !== 'header' }))
    if (selection.nodes?.length && nodes.size === 0) return true
    if (backward && anchorIndex === 0 && nodes.size === 0) return true
    const index = nodes.size > 0 ? state.blocks.findIndex((block) => nodes.has(block.id)) : anchorIndex - (backward ? 1 : 0)
    // Lexical deleteCharacter at a block cursor: the atom or grid beside it goes (`$needsBlockCursorBeside`).
    const beside = state.blocks[index] as Block | undefined
    if (!beside || !nodes.has(beside.id) && !['atom', 'grid'].includes(blockClass(beside))) return false
    const table = state.blocks[index]?.type === 'table'
    if (nodes.size === 0) spliceBlocks(state, state.blocks, index, 1)
    for (let at = state.blocks.length - 1; at >= index; at -= 1) if (nodes.has(state.blocks[at]?.id ?? '')) spliceBlocks(state, state.blocks, at, 1)
    if (state.blocks.every(block => blockClass(block) === 'header')) {
      // A page left with only its header gets its body paragraph back (Lexical `$restoreEmptyContainerParagraph`).
      const paragraph: Block = { id: '', type: 'p', children: [] }
      spliceBlocks(state, state.blocks, state.blocks.length, 0, paragraph)
      state.selection = { anchor: { blockId: paragraph.id, offset: 0 }, focus: { blockId: paragraph.id, offset: 0 } }
      return true
    }
    const adjacent = state.blocks[index] ?? state.blocks[index - 1]
    const next = state.blocks[index]
    const candidates = table && adjacent ? rows([adjacent]) : []
    const target = next ? candidates[0] : candidates.at(-1)
    const point = target ? pointAt(target, next ? 0 : plain(target.block).length)
      : gapPoint({ siblings: state.blocks, index })
    state.selection = { anchor: point, focus: point }
    return true
  }
  if (intent.type !== 'moveCaret' || intent.extend || intent.unit !== 'character') return false
  const backward = intent.direction === 'backward'
  const direction = backward ? -1 : 1
  // From a node selection, wherever it sits: onto the next atom, else into the next text, else out of its container.
  if (selection.nodes?.length) {
    const gap = blockGap(state.blocks, selection.nodes[0])
    const block = gap?.siblings[gap.index + direction]
    if (!gap || (!block && gap.siblings === state.blocks)) return true
    if (block && isSelectableBlock(block)) {
      state.selection = nodeSelection(block.id)
      return true
    }
    const target = block && (backward ? rows([block]).at(-1) : rows([block])[0])
    const point = target ? pointAt(target, backward ? plain(target.block).length : 0)
      : block ? undefined : gapPoint({ ...gap, index: gap.index + (backward ? 0 : 1) })
    if (point) state.selection = { anchor: point, focus: point }
    return true
  }
  const position = locate(entries, selection.focus)
  const row = entries[position.row]
  const block = row.parents[0] ?? row.block
  const index = root ? focusIndex - (backward ? 1 : 0)
    : state.blocks.indexOf(block) + direction
  const adjacent = state.blocks[index]
  if (root && adjacent?.type === 'table') {
    const target = backward ? rows([adjacent]).at(-1) : rows([adjacent])[0]
    if (target) {
      const point = pointAt(target, backward ? plain(target.block).length : 0)
      state.selection = { anchor: point, focus: point }
    }
    return true
  }
  if (!root && !backward && block.type === 'table' &&
    position.offset === plain(row.block).length && rows([block]).at(-1)?.block === row.block &&
    state.blocks.at(-1) === block) {
    const point = { blockId: block.id, offset: 1 }
    state.selection = { anchor: point, focus: point }
    return true
  }
  if (!root && backward && block.type === 'table' && position.offset === 0 &&
    rows([block])[0]?.block === row.block && state.blocks[0] === block) {
    const point = { blockId: block.id, offset: 0 }
    state.selection = { anchor: point, focus: point }
    return true
  }
  // Lexical `$shouldOverrideDefaultCharacterSelection`: the caret at a row edge moves onto the atom beside that row,
  // in the row's own block list, else beside its top-level block when the row is that block's edge row.
  const edge = position.offset === (backward ? 0 : plain(row.block).length)
  const edgeOfTop = (backward ? rows([block])[0] : rows([block]).at(-1))?.block === row.block
  const beside = () => row.siblings[row.siblings.indexOf(row.block) + direction] ?? (edgeOfTop ? adjacent : undefined)
  const neighbour = root ? adjacent : edge ? beside() : undefined
  if (!neighbour || !isSelectableBlock(neighbour)) return false
  state.selection = nodeSelection(neighbour.id)
  return true
}
